import type { FormatId } from "../editorial/style.js";
import { normalizeText } from "../editorial/text.js";
import { DIRECTION_INFO, type VisualDirectionId } from "./direction.js";

/**
 * Image rules calibrated on the OPULANCIA library (docs/VISUAL_CALIBRATION.md): framing, light,
 * realism, mood, room for the text and coherence between slides. Sources: the 41 originals
 * already exported and checked by sha256 (25 covers, 16 inner or end slides), the stored measures
 * of the 244 slides (colours, split compositions, bottom darkening) and the two archived
 * references (kakapo, prix avant/après). No OCR, no paid call, no generation.
 *
 * The ranges describe what was published, not what performs. They write the image briefs of
 * « Préparer un carrousel » for the person who makes or finds the images; they never generate one.
 * Pure, no Node dependency: the web app's Atelier imports this file.
 */

/** observé: what the library shows; repère: seen on part of it only, a landmark, never a constraint; choix: production decision; à arbitrer: to settle. */
export type RuleStatus = "observé" | "repère" | "choix" | "à arbitrer";

export interface VisualRule {
  id: string;
  label: string;
  /** What the brief asks, in plain words. */
  rule: string;
  status: RuleStatus;
  /** Library examples as `dNN` (deposit) or `dNN_sPP` (deposit, slide). */
  examples: string[];
}

/** Common to every format, unless its profile says otherwise. */
export const VISUAL_COMMON: VisualRule[] = [
  {
    id: "text_room",
    label: "Place du texte",
    rule: "le tiers bas (environ 35 à 40 % de la hauteur) reste sombre ou simple, sans détail important : le titre s'y pose ; sinon le fondu au noir de la mise en page le crée",
    status: "observé",
    examples: ["d03_s01", "d09_s01", "d21_s01", "d25_s01"],
  },
  {
    id: "subject_zone",
    label: "Sujet",
    rule: "un sujet principal, entier et reconnaissable en moins d'une seconde, centré entre 10 % et 60 % de la hauteur ; le haut du sujet reste sous la marque",
    status: "observé",
    examples: ["d04_s01", "d07_s05", "d20_s01", "d25_s11"],
  },
  {
    id: "logo_room",
    label: "Place de la marque",
    rule: "la bande du haut (environ 10 %) est moyenne à sombre ou uniforme (ciel, mur, feuillage) : la marque blanche y reste lisible ; éviter une fenêtre surexposée ou un soleil derrière elle",
    status: "choix",
    examples: ["d02_s01", "d23_s01", "d10_s01 (marque sur fenêtre claire, à éviter)"],
  },
  {
    id: "light",
    label: "Lumière",
    rule: "une lumière directionnelle par image, choisie pour le sujet (dorée rasante, clair-obscur, plein jour, nuit…) ; elle change quand le lieu ou le récit l'exige (kakapo 05 : la nuit, dans une série dorée) ; la cohérence du carrousel vient du traitement (contraste, netteté, étalonnage), pas d'une lumière unique",
    status: "repère",
    examples: ["d03_s01", "d19_s01 (dorée)", "d15_s01", "d17_s06 (clair-obscur)", "d10_s01", "d22_s01 (plein jour)", "kakapo 05 (nuit, récit)"],
  },
  {
    id: "realism",
    label: "Représentation",
    rule: "choisie selon le sujet et les références, sans hiérarchie : photo documentaire, mise en scène photoréaliste (kakapo 01), composite, mise en scène spectaculaire ou humoristique (animaux sur une piste ou un podium, appartement en orbite), illustration ; une métaphore peut inventer un décor, jamais suggérer une capacité fausse ; l'image illustre le fait sans en être la preuve et ne passe jamais pour la photo d'un événement réel",
    status: "observé",
    examples: ["d03_s01", "d04_s01", "d14_s01", "d13 (exception de style : échelle cosmique)", "kakapo 01"],
  },
  {
    id: "real_person",
    label: "Personne réelle",
    rule: "une personne réelle reconnaissable ne se génère pas : photo sous licence, ou image sans visage reconnaissable ; jamais dans une situation inventée",
    status: "choix",
    examples: ["d15_s01", "d16_s01", "d17_s01 (droits non documentés, signalés)"],
  },
  {
    id: "mood",
    label: "Ambiance",
    rule: "une émotion lisible portée par la couverture : émerveillement, tension, humour (expression animale), nostalgie, curiosité ; les slides suivantes peuvent la faire varier quand le sujet change, si l'ensemble reste lisible (observé sur les couvertures, rarement sur les séries entières)",
    status: "repère",
    examples: ["d19_s01 (émerveillement)", "d25_s01 (tension)", "d11_s03 (humour)", "d12_s01 (nostalgie)"],
  },
  {
    id: "coherence",
    label: "Cohérence",
    rule: "même traitement (netteté, contraste, étalonnage) et même type de représentation sur toutes les slides, sauf raison éditoriale ; la dernière slide reprend le monde de la couverture (lieu, personnage ou décor)",
    status: "observé",
    examples: ["d09_s08", "d19_s14", "d22_s12", "d23_s10", "d24_s08", "d25_s11", "kakapo 01–09"],
  },
  {
    id: "text_in_image",
    label: "Texte dans l'image",
    rule: "aucun texte, chiffre, logo ni typographie demandé à l'image : la mise en page les pose ; exception à arbitrer : les tickets de la famille « prix avant / après », validée le 3 octobre 2026",
    status: "à arbitrer",
    examples: ["d05_s01", "prix avant/après 01–06"],
  },
];

export type SlideRole = "cover" | "inner" | "end";

export interface VisualProfile {
  format: FormatId;
  /** Visual directions that fit the format (docs/VISUAL_DIRECTION_V1.md), the usual one first. */
  directions: VisualDirectionId[];
  framing: string;
  light: string;
  /** A light shared by the series is expected (a comparison, one character in one place); lists and stories follow each slide's subject. */
  oneLight: boolean;
  /** How the inner slides relate to the cover and to each other. */
  series: string;
  /** Inner slides without text need no darkened bottom. */
  textlessInner: boolean;
  examples: string[];
  notes: string[];
}

export const VISUAL_PROFILES: Record<FormatId, VisualProfile> = {
  F1: {
    format: "F1",
    directions: ["spectacle", "scale"],
    framing: "un sujet par slide, centré, plan moyen ou large ; la scène illustre l'élément, pas un décor générique",
    light: "celle du sujet de chaque élément (dorée, plein jour, nuit, froide pour un sujet futuriste)",
    oneLight: false,
    series: "chaque élément a sa scène et sa lumière, avec un traitement commun ; une même vue peut revenir sous un autre angle",
    textlessInner: false,
    examples: ["d03_s01", "d04_s01", "d06_s01", "d18_s01", "d18_s03", "d23_s01", "d23_s10"],
    notes: ["listes numérotées : le numéro cerclé se pose au-dessus du titre, dans la zone sombre"],
  },
  F2: {
    format: "F2",
    directions: ["before_after", "evidence"],
    framing: "deux états côte à côte, coupe verticale au centre, même point de vue, même hauteur, même cadrage ; ou une même scène partagée en deux",
    light: "même lumière des deux côtés, sauf si la lumière est l'objet de la comparaison (couleurs contre gris)",
    oneLight: true,
    series: "les paires gardent le même axe d'une slide à l'autre ; les slides intérieures peuvent être des photos sans texte",
    textlessInner: true,
    examples: ["d01_s01", "d08_s01", "d12_s01", "d18_s01", "d24_s01", "d20_s05 (fiche : photo en haut, panneau en bas)"],
    notes: ["la ligne de coupe s'efface dans la zone du titre", "étiquettes AVANT / APRÈS posées par la mise en page, pas dans l'image"],
  },
  F3: {
    format: "F3",
    directions: ["spectacle"],
    framing: "un seul personnage ou animal, expressif ; du plan moyen au très gros plan au fil des slides",
    light: "plein jour ou lumière froide de lieu clos",
    oneLight: true,
    series: "même personnage, même lieu ; les photos intérieures n'ont pas de texte, donc pas de fondu",
    textlessInner: true,
    examples: ["d09_s01", "d09_s08", "d11_s01", "d11_s03", "d21_s01"],
    notes: ["l'accroche porte la chute : l'image de couverture montre la situation, pas la réponse"],
  },
  F4: {
    format: "F4",
    directions: ["spectacle", "evidence"],
    framing: "portrait à mi-corps de la personne citée, ou d'un personnage qui l'évoque, en haut du cadre ; la citation occupe la moitié basse",
    light: "clair-obscur intérieur, chaud",
    oneLight: true,
    series: "même personne, même décor ou même univers sur toutes les citations",
    textlessInner: false,
    examples: ["d15_s01", "d17_s01", "d17_s06", "d22_s01", "d22_s12"],
    notes: ["personne réelle : photo sous licence ou pas de visage reconnaissable (règle commune)"],
  },
  F5: {
    format: "F5",
    directions: ["spectacle"],
    framing: "un animal ou un sujet de face, plein cadre, sur fond de studio uni ou dans un décor domestique simple",
    light: "lumière de studio douce ou plein jour intérieur",
    oneLight: true,
    series: "même sujet sur fond constant ; l'expression porte l'étiquette",
    textlessInner: false,
    examples: ["d07_s01", "d07_s05", "d10_s01", "d10_s06"],
    notes: ["piste : deux exemples seulement"],
  },
  F6: {
    format: "F6",
    directions: ["spectacle", "evidence"],
    framing: "plan large ou vue aérienne pour situer, puis plus près à chaque étape",
    light: "celle du lieu et du moment du récit ; elle change avec le temps du récit",
    oneLight: false,
    series: "même monde, ordre du temps ou de l'espace ; la fin reprend le sujet du récit",
    textlessInner: false,
    examples: ["d14_s01", "d14_s04", "d25_s01", "d25_s11"],
    notes: ["piste : deux exemples seulement", "d14_s04 : texte sur un sol clair, contraste faible, à éviter"],
  },
  "F1-choice": {
    format: "F1-choice",
    directions: ["spectacle"],
    framing: "panorama large vu depuis un premier plan constant (balcon, fenêtre) : seul le paysage change",
    light: "lumière spectaculaire mais propre à chaque lieu ; le premier plan garde la même",
    oneLight: false,
    series: "le premier plan constant fait la cohérence ; la fin rassemble les options",
    textlessInner: false,
    examples: ["d19_s01", "d19_s05", "d19_s14"],
    notes: ["piste : un exemple seulement"],
  },
};

export interface BriefLinesInput {
  format: FormatId;
  role: SlideRole;
  /** The slide carries a title or a text block. */
  hasText: boolean;
  direction?: VisualDirectionId;
}

/** The lines a brief carries for one slide: common rules, then the format, then the direction. */
export function visualBriefLines(input: BriefLinesInput): string[] {
  const profile = VISUAL_PROFILES[input.format];
  const lines: string[] = [];
  lines.push(`Cadrage : ${profile.framing}.`);
  lines.push(`Lumière : ${profile.light}${profile.oneLight ? " ; une lumière commune à la série, sauf raison du récit" : ""}.`);
  lines.push("Sujet : entier, reconnaissable en moins d'une seconde, entre 10 % et 60 % de la hauteur, sous la marque.");
  lines.push("Haut de l'image : bande moyenne à sombre ou uniforme, pour la marque blanche.");
  if (input.hasText) lines.push("Bas de l'image : le tiers bas sombre ou simple, pour le titre.");
  else if (!profile.textlessInner) lines.push("Slide sans texte : inhabituel pour ce format ; vérifier.");
  lines.push("Aucun texte, chiffre, logo ni typographie dans l'image : la mise en page les pose.");
  if (input.role === "inner") lines.push(`Série : ${profile.series}.`);
  if (input.role === "end") lines.push("Fin : reprendre le monde de la couverture (lieu, personnage ou décor).");
  if (input.direction) lines.push(DIRECTION_INFO[input.direction].prompt);
  return lines;
}

export interface BriefSlide {
  role: SlideRole;
  hasText: boolean;
  /** The brief as written by a person or a model. */
  brief: string;
  realism?: "photo" | "archive" | "composite" | "ai_photoreal" | "illustration";
  light?: string;
  /** Set by a person: the image shows a real, recognisable person. Never inferred. */
  realPerson?: boolean;
}

export interface VisualIssue {
  code: string;
  level: "info" | "warning" | "blocking";
  message: string;
}

// what asks for writing in the image (« lisible » alone describes a clear subject: only a word, a
// sign or a label « lisible » asks for text; « légende » is too often the story to count)
const ASKS_TEXT = /\b(on lit|ecrit|ecrite|ecrits|ecrites|inscription|inscriptions|logo|logos|typographie|(?:texte|textes|mot|mots|lettre|lettres|chiffre|chiffres|nom|slogan|enseigne|panneau|etiquette|ecriture)(?: \p{L}+){0,2} lisibles?)\b/gu;
// a price or a sum (a percentage describes the layout: « occupe 40 % du cadre »)
const AMOUNT = /\d[\d\s.,]*\s?(€|eur|euros?|francs?|f|\$)(?!\p{L})/giu;
// « sans logo », « aucun texte lisible », « pas de texte, de chiffres, de logos ou de typographie » forbid rather than ask
const NEGATION = /\b(sans|aucun|aucune|ni|pas|jamais|evite|eviter|evitez|interdit|interdits|exclu|exclus|proscrit|non)\b/;
// « un haut calme pour le logo », « la zone du titre et du logo »: room left for the layout, not a request
const ROOM = /\b(pour|place|espace|zone|reserve|reservee|laisser|laisse|libre|calme|sous|dessus|derriere|degage|degagee)\b/;

/** The first word that asks for text in one clause of a brief, or null (negated and layout mentions set aside). */
function asks(pattern: RegExp, clause: string, raw: string): string | null {
  for (const m of clause.matchAll(pattern)) {
    const before = normalizeText(clause.slice(0, m.index)).split(" ").filter(Boolean);
    if (NEGATION.test(before.slice(-12).join(" "))) continue;
    if (pattern === ASKS_TEXT && ROOM.test(before.slice(-5).join(" "))) continue;
    return pattern === AMOUNT ? (raw.match(AMOUNT)?.[0]?.trim() ?? m[0]) : m[0];
  }
  return null;
}
const GENERATED = new Set(["composite", "ai_photoreal", "illustration"]);

/**
 * Families of light, to compare a series by what the light is, not by how it is worded (two
 * briefs that say « dorée rasante » and « lumière dorée de fin de journée » ask the same light).
 * The first family that matches wins; a light none matches is not counted.
 */
const LIGHT_FAMILIES: Array<[string, RegExp]> = [
  ["heure dorée", /\b(dore|doree|dorees|golden|coucher|couchant|fin de journee|fin d apres midi)\b/],
  ["heure bleue", /\b(heure bleue|crepuscule|crepusculaire|blue hour)\b/],
  ["aube", /\b(aube|aurore|lever du soleil|levant|petit matin|matinale)\b/],
  ["nuit", /\b(nuit|nocturne|etoile|etoiles|etoilee|lune|lunaire)\b/],
  ["intérieur ou artificielle", /\b(interieur|studio|lampe|lampes|bougie|bougies|neon|neons|artificielle|eclairage)\b/],
  ["temps couvert", /\b(couvert|nuageux|diffuse|brume|brumeux|brouillard|grise|gris|orage|orageux|pluie)\b/],
  ["plein jour", /\b(midi|plein jour|plein soleil|zenith|journee|ensoleille|ensoleillee|soleil|jour)\b/],
];
export function lightFamily(light: string): string | null {
  const text = normalizeText(light);
  return LIGHT_FAMILIES.find(([, re]) => re.test(text))?.[0] ?? null;
}

/** What to fix in a set of briefs before images are made or chosen. */
export function visualBriefIssues(slides: BriefSlide[], direction?: VisualDirectionId, format?: FormatId): VisualIssue[] {
  const issues: VisualIssue[] = [];
  const add = (code: string, level: VisualIssue["level"], message: string) => issues.push({ code, level, message });
  slides.forEach((s, i) => {
    const n = i + 1;
    // one clause at a time: a negation or a mention of room for the layout only covers its own clause
    const clauses = s.brief.split(/[.;:!?\n·]+/);
    let word: string | null = null;
    for (const raw of clauses) {
      word = asks(ASKS_TEXT, normalizeText(raw), raw) ?? asks(AMOUNT, raw.toLowerCase(), raw);
      if (word) break;
    }
    if (word) {
      const generated = s.realism !== undefined && GENERATED.has(s.realism);
      add(
        "TEXT_IN_IMAGE",
        direction === "evidence" && generated ? "blocking" : "warning",
        `slide ${n} : le brief semble demander du texte, un chiffre ou un logo dans l'image (mot repéré : « ${word} ») ; la mise en page les pose${direction === "evidence" && generated ? " (image générée en direction « preuve » : jamais un faux document)" : ""}`,
      );
    }
    if (s.realPerson && (s.realism === undefined || GENERATED.has(s.realism))) {
      add("REAL_PERSON_GENERATED", "blocking", `slide ${n} : personne réelle reconnaissable ; photo sous licence ou image sans visage reconnaissable`);
    }
  });
  if (!format || VISUAL_PROFILES[format].oneLight) {
    const families = new Map<string, number[]>();
    slides.forEach((s, i) => {
      const family = s.light ? lightFamily(s.light) : null;
      if (family) families.set(family, [...(families.get(family) ?? []), i + 1]);
    });
    if (families.size > 1) {
      const list = [...families].map(([f, n]) => `${f} : slide${n.length > 1 ? "s" : ""} ${n.join(", ")}`).join(" ; ");
      add("SERIES_LIGHT_MIX", "info", `${families.size} familles de lumière dans le carrousel (${list}) : à vérifier seulement si la série devait partager une lumière (même lieu, même personnage, comparaison)`);
    }
  }
  const realisms = new Set(slides.map((s) => s.realism).filter(Boolean));
  if (realisms.size > 1) add("SERIES_REALISM_MIX", "warning", `réalismes mélangés (${[...realisms].join(", ")}) ; même niveau sur tout le carrousel, sauf raison éditoriale`);
  return issues;
}
